/*
Color switcher — client-side swap of a single preview image driven by a row of
round color-swatch buttons (campaign-color-switcher section, legacy "mappa
stellare" design parity).

Zero dependencies. Native <button> elements give Enter/Space activation for
free, so this only needs a click listener (event delegation on the host).
Clicking a swatch sets the preview <img> src/alt to that swatch's
data-image/data-image-alt and marks it selected (aria-pressed + a --selected
class ring). The first swatch is selected on load (server-rendered).

HTML usage:
<color-switcher>
  <img class="campaign-color-switcher__preview" src="…" alt="…" width="458" height="595">
  <div class="campaign-color-switcher__swatches" role="group" aria-label="…">
    <button type="button" class="campaign-color-switcher__swatch campaign-color-switcher__swatch--selected"
      style="--swatch-color: #2D2C4D" data-image="…" data-image-alt="…" aria-pressed="true" aria-label="Colore 1"></button>
    …
  </div>
</color-switcher>
*/

class ColorSwitcher extends HTMLElement {
  connectedCallback() {
    this.swatches = [...this.querySelectorAll('.campaign-color-switcher__swatch')];
    this.preview = this.querySelector('.campaign-color-switcher__preview');
    if (this.swatches.length === 0 || !this.preview) return;

    this.onClick = (event) => this.#handleClick(event);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  #handleClick(event) {
    const swatch = event.target.closest('.campaign-color-switcher__swatch');
    if (!swatch || !this.contains(swatch)) return;

    this.#select(swatch);
  }

  #select(activeSwatch) {
    const { image, imageAlt } = activeSwatch.dataset;
    if (image) this.preview.src = image;
    if (imageAlt) this.preview.alt = imageAlt;

    for (const swatch of this.swatches) {
      const isSelected = swatch === activeSwatch;
      swatch.classList.toggle('campaign-color-switcher__swatch--selected', isSelected);
      swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    }
  }
}

customElements.define('color-switcher', ColorSwitcher);
